import { Controller } from "@hotwired/stimulus"

// The new music video modal: pick a track and inherit its metadata, or fill in
// the few fields a blank draft needs.
//
// Creating from a track posts to the same endpoint the album review page uses,
// so there is one copy routine, not two.
export default class extends Controller {
  static targets = [
    "trackTab", "manualTab", "trackPanel", "manualPanel",
    "searchInput", "results", "searchBlock",
    "selection", "selectedTitle", "selectedMeta",
    "trackForm", "trackSubmit"
  ]

  static values = {
    searchUrl: String,
    searchingText: String,
    noResultsText: String,
    existingText: String
  }

  static ACTIVE_TAB = [ "bg-white", "text-gray-900", "shadow-sm" ]
  static IDLE_TAB = [ "text-gray-500", "hover:text-gray-900" ]

  connect() {
    this.showTrack()
  }

  // ---------- tabs ----------

  showTrack() {
    this.setTab(this.trackTabTarget, this.manualTabTarget)
    this.trackPanelTarget.classList.remove("hidden")
    this.manualPanelTarget.classList.add("hidden")
    // Focusing here is the point of the tab, but not on a phone where it would
    // throw the keyboard over the modal the moment it opens.
    if (window.innerWidth >= 640) this.searchInputTarget.focus()
  }

  showManual() {
    this.setTab(this.manualTabTarget, this.trackTabTarget)
    this.manualPanelTarget.classList.remove("hidden")
    this.trackPanelTarget.classList.add("hidden")
  }

  setTab(active, idle) {
    active.classList.add(...this.constructor.ACTIVE_TAB)
    active.classList.remove(...this.constructor.IDLE_TAB)
    idle.classList.remove(...this.constructor.ACTIVE_TAB)
    idle.classList.add(...this.constructor.IDLE_TAB)
  }

  // ---------- search ----------

  search() {
    const query = this.searchInputTarget.value.trim()

    clearTimeout(this.searchTimeout)

    if (query.length < 2) {
      this.hideResults()
      return
    }

    // Typing a song title fires a lot of keystrokes; one request per pause.
    this.searchTimeout = setTimeout(() => this.runSearch(query), 250)
  }

  async runSearch(query) {
    this.showMessage(this.searchingTextValue)

    try {
      const response = await fetch(`${this.searchUrlValue}?query=${encodeURIComponent(query)}`, {
        headers: { "Accept": "application/json" }
      })
      if (!response.ok) return this.hideResults()

      const tracks = await response.json()

      // A slow answer to an older query must not overwrite a newer one.
      if (this.searchInputTarget.value.trim() !== query) return

      this.renderResults(tracks)
    } catch (error) {
      this.hideResults()
    }
  }

  renderResults(tracks) {
    if (tracks.length === 0) return this.showMessage(this.noResultsTextValue)

    this.resultsTarget.innerHTML = ""

    tracks.forEach(track => {
      const row = document.createElement(track.existing_video_path ? "a" : "button")

      if (track.existing_video_path) {
        // Already has a video: offer the video rather than a second one.
        row.href = track.existing_video_path
        row.className = "block w-full px-3 py-2 text-left hover:bg-gray-50 cursor-pointer"
      } else {
        row.type = "button"
        row.className = "block w-full px-3 py-2 text-left hover:bg-gray-50 cursor-pointer"
        row.addEventListener("click", () => this.selectTrack(track))
      }

      row.innerHTML = `
        <span class="block text-sm font-medium text-gray-900 truncate">${this.escape(track.title)}</span>
        <span class="block text-xs text-gray-500 truncate">${this.escape(this.metaFor(track))}</span>
        ${track.existing_video_path ? `<span class="mt-1 inline-flex items-center rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">${this.escape(this.existingTextValue)}</span>` : ""}
      `

      this.resultsTarget.appendChild(row)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  showMessage(text) {
    this.resultsTarget.innerHTML = `<p class="px-3 py-2 text-sm text-gray-500">${this.escape(text)}</p>`
    this.resultsTarget.classList.remove("hidden")
  }

  hideResults() {
    this.resultsTarget.innerHTML = ""
    this.resultsTarget.classList.add("hidden")
  }

  // ---------- selection ----------

  selectTrack(track) {
    this.trackFormTarget.action = track.create_path
    this.trackSubmitTarget.disabled = false

    this.selectedTitleTarget.textContent = track.title
    this.selectedMetaTarget.textContent = this.metaFor(track)

    this.selectionTarget.classList.remove("hidden")
    this.searchBlockTarget.classList.add("hidden")
    this.hideResults()
  }

  clearSelection() {
    this.trackFormTarget.action = ""
    this.trackSubmitTarget.disabled = true

    this.selectionTarget.classList.add("hidden")
    this.searchBlockTarget.classList.remove("hidden")
    this.searchInputTarget.value = ""
    this.searchInputTarget.focus()
  }

  metaFor(track) {
    return [ track.artists, track.album_title, track.organization ].filter(Boolean).join(" · ")
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = value == null ? "" : value
    return div.innerHTML
  }
}
